import React from 'react';
import { DeepMap, FieldError, FieldValues } from 'react-hook-form';
import RichTextEditorV2 from '../../../layouts/form/RichTextEditorV2';
import { TemplateValidation } from '../../../models/TemplateValidation';
import Modal from '../../../layouts/modals/Modal';
import { ModalContent } from '../../../models/ModalContent';
import Text from '../../../layouts/typography/Text';
import Title from '../../../layouts/typography/Title';
import Button from '../../../layouts/buttons/Button';
import ic_alert from '../../../assets/img/ic_alert.svg';

export interface IEditorChangeEvent {
    currentTarget: {
        id: string;
        dataset: {
            index?: number;
            sectionindex?: number;
        };
        value: string;
    };
    target: {
        value: string;
    };
}

interface IEditorProps extends React.ComponentPropsWithoutRef<'input'> {
    id: string;
    label: string;
    errors?: DeepMap<FieldValues, FieldError>;
    validations?: TemplateValidation;
    defaultValue?: string;
    children?: React.ReactNode;
    index?: number;
    sectionIndex?: number;
    onChange?: (e: IEditorChangeEvent) => void;
}

const Editor = React.forwardRef((props: IEditorProps, ref: React.Ref<HTMLInputElement>) => {
    const [editorState, setEditorState] = React.useState(props.defaultValue);
    const editorContainerRef = React.createRef<HTMLDivElement>();
    const [hasError, setHasError] = React.useState(false);
    const { errors, id } = props;
    let editorInstance: any = null;

    const [isModalVisible, setIsModalVisible] = React.useState(false);
    const initialModal = {
        loading: true,
        title: '',
        subtitle: '',
        buttons: [
            {
                label: '',
                event: () => {
                    return;
                },
            },
        ],
    };
    const [modalContent, setModalContent] = React.useState<ModalContent>(initialModal);

    React.useEffect(() => {
        if (errors && errors[id]) {
            setHasError(true);
            editorContainerRef.current?.scrollIntoView();
        }
    }, [errors]);

    const onEditorReady = (event: any) => {
        editorInstance = event;
        if (editorInstance) {
            editorInstance.editing.view.change((writer: any) => {
                writer.setStyle('min-height', '325px', editorInstance.editing.view.document.getRoot());
            });
        }
    };

    const onEditorChanged = (event: any, editor: any) => {
        if (hasError) {
            setHasError(false);
        }
        setEditorState(editor.getData());
        const e: IEditorChangeEvent = {
            currentTarget: {
                id: id,
                dataset: {
                    index: props.index,
                    sectionindex: props.sectionIndex,
                },
                value: editor.getData(),
            },
            target: {
                value: editor.getData(),
            },
        };
        if (props.onChange) {
            const data = editor.getData();
            const sizeInBytes = new TextEncoder().encode(data).length;
            const sizeInMB = sizeInBytes / (1024 * 1024);
            console.log(sizeInMB);
            if (sizeInMB > 1) {
                setModalContent({
                    loading: false,
                    icon: ic_alert,
                    title: 'Your Memo exceeded the 1MB limit',
                    subtitle: (
                        <div className="text-left">
                            <p>
                                The pasted images in the Content field made your Memo too large. To fix this, you can:
                            </p>
                            <br />
                            <ul className="list-disc pl-10">
                                <li>Reduce the number/size of images, or</li>
                                <li>Save your images in Google Drive and paste the link in the Content field.</li>
                            </ul>
                        </div>
                    ),
                    buttons: [
                        {
                            label: 'Okay, got it',
                            event: async () => {
                                setIsModalVisible(false);
                            },
                        },
                    ],
                });
                setIsModalVisible(true);
            }

            props.onChange(e);
        }
        // dispatch(setEditorState(editor.getData()));
        // Emit the data back to form
        return event;
    };

    return (
        <div>
            <RichTextEditorV2
                {...props}
                ref={ref}
                editorContainerRef={editorContainerRef}
                editorInstance={editorInstance}
                hasError={hasError}
                data={editorState}
                onReady={onEditorReady}
                onChange={onEditorChanged}
            />
            <Modal visible={isModalVisible}>
                {modalContent.loading ? (
                    <div className="w-full flex flex-col items-center justify-center">
                        <Text>Loading...</Text>
                    </div>
                ) : (
                    <div className="w-full flex flex-col items-center">
                        <img src={modalContent.icon} style={{ width: '120px', height: '120px' }} />
                        <Title type="h4">{modalContent.title}</Title>
                        <div className="my-2 text-center">
                            <Text>{modalContent.subtitle}</Text>
                        </div>
                        {modalContent.buttons.length > 1 ? (
                            <div className="w-full my-2 flex flex-col-reverse md:flex-row justify-center">
                                <div className="w-full flex flex-col px-2">
                                    <Button
                                        type="button"
                                        color="secondary"
                                        weight="font-regular"
                                        onClick={modalContent.buttons[0].event}
                                    >
                                        {modalContent.buttons[0].label}
                                    </Button>
                                </div>
                                <div className="w-full flex flex-col px-2">
                                    <Button
                                        color="primary"
                                        weight="font-regular"
                                        type="button"
                                        onClick={modalContent.buttons[1].event}
                                    >
                                        {modalContent.buttons[1].label}
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            <div className="my-2">
                                <Button
                                    type="button"
                                    color="primary"
                                    weight="font-regular"
                                    onClick={modalContent.buttons[0].event}
                                >
                                    {modalContent.buttons[0].label}
                                </Button>
                            </div>
                        )}
                    </div>
                )}
            </Modal>
        </div>
    );
});

Editor.displayName = 'Editor';
export default Editor;
